import { ChangeDetectionStrategy, Component, HostListener, inject, OnInit, signal } from '@angular/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ComponentCanDeactivate, ConfigSettingsService } from '@ebeihilfe/core';
import { Antrag, EinreichungStep, OldCoreEinreichungModule } from '@ebeihilfe/einreichung-old';
import { ModalService } from '@ebeihilfe/web-logic';
import { TranslateService } from '@ngx-translate/core';

import { CustomComponentModule } from '../../components';
import { StepperComponent } from './stepper/stepper.component';

@Component({
  selector: 'portal-einreichung',
  templateUrl: 'portal-einreichung.component.html',
  styleUrls: ['./portal-einreichung.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CustomComponentModule, StepperComponent, OldCoreEinreichungModule, MatProgressSpinner],
})
export class PortalEinreichungComponent implements OnInit, ComponentCanDeactivate {
  private readonly configService = inject(ConfigSettingsService);
  private readonly modalService = inject(ModalService);
  private readonly translate = inject(TranslateService);
  private readonly antrag = signal<Antrag | null>(null);

  public currentStep = signal<EinreichungStep | null>(null);
  public maximumAntragSizeInMb = signal<number>(0);
  public maximumFileSizeInMb = signal<number>(0);
  public showSpinner = signal<boolean>(false);

  @HostListener('window:beforeunload') public unload(): void {
    // navigating to external sites can't be prevented from within angular
    // just need some stub function to get called when user clicks some link
  }

  public antragChangedEmitter(antrag: Antrag): void {
    this.antrag.set(antrag);
  }

  public fromEinreichungsStepEmitter(step: EinreichungStep): void {
    this.currentStep.set(step);
  }

  public hasUiEmitter(hasUi: boolean): void {
    this.showSpinner.set(!hasUi);
  }

  public canDeactivate(): Promise<boolean> {
    const antrag = this.antrag();

    if (antrag?.isComplete) return Promise.resolve(true);
    if (!antrag?.hasAntragsMetadata() && !antrag?.hasSeiten()) return Promise.resolve(true);

    return this.modalService.openConfirmationModal({
      content: this.translate.instant('new_antrag.text.discarding_all_data'),
    });
  }

  public ngOnInit(): void {
    const constants = this.configService.appSettings.constants;
    this.maximumFileSizeInMb.set(constants.maximumFileSizeInMb);
    this.maximumAntragSizeInMb.set(constants.maximumAntragSizeInMb);
  }
}
